import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Ban,
  Check,
  Eye,
  EyeOff,
  HelpCircle,
  Info,
  Minimize2,
  ScanFace,
  Sparkles,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  photoAge,
  photoDateConflict,
  type FuzzyDate,
  type MediaRegion,
  type PersonRef,
} from "@familienbaum/shared";
import { Button } from "@/components/ui/button";
import { CursorCard, type CursorPoint } from "@/components/ui/cursor-card";
import { Input } from "@/components/ui/field";
import { PersonPicker } from "@/components/person-picker";
import { PhotoFrame } from "@/components/ui/paper";
import {
  DETECTION_PASSES,
  detectFaces,
  detectorReady,
  withoutExisting,
  type DetectedFace,
} from "@/lib/faces/detect";
import { cn } from "@/lib/utils";
import { useRegionVisibility } from '@/lib/view-preferences';
import type { Box } from "./face-crop";

const MIN_SIZE = 0.02; // 2 % der Bildkante – darunter ist es ein Fehlklick
const NUDGE = 0.005;

/**
 * Schnellrahmen: Breite zu Höhe. 3:4 heißt Hochformat – ein Rahmen um Kopf
 * und Schultern, etwas kompakter als das schlanke 9:16.
 */
const QUICK_RATIO = 3 / 4;

/**
 * Höhe des Schnellrahmens als Anteil der Bildhöhe, bei Zoomstufe 1.
 *
 * Geteilt wird durch den Zoom, der Rahmen erscheint auf dem Bildschirm also
 * immer gleich groß. Das ist der eigentliche Trick für kleine Gesichter in
 * Gruppenbildern: heranzoomen, tippen – der Rahmen fällt von allein
 * entsprechend kleiner aus.
 */
const QUICK_HEIGHT = 0.2;

/** Zoomgrenzen. Unter 1 wird nicht verkleinert – das Bild passt ja schon. */
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.4;

type Handle = "nw" | "ne" | "se" | "sw";

interface DragState {
  pointerId: number;
  kind: "draw" | "move" | "resize";
  handle?: Handle;
  origin: { x: number; y: number };
  startBox: Box;
  regionId?: string;
}

/** Zoomstufe und Verschiebung des Bildes im Ausschnitt (Verschiebung in px). */
interface View {
  zoom: number;
  x: number;
  y: number;
}

const FIT: View = { zoom: 1, x: 0, y: 0 };

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(1, value));
}

/**
 * Verschiebung so begrenzen, dass kein Rand frei bleibt: das vergrößerte Bild
 * ist `zoom`-mal so groß wie der Ausschnitt, also darf es maximal um die
 * Differenz nach links/oben rutschen.
 */
function clampPan(
  x: number,
  y: number,
  zoom: number,
  rect: { width: number; height: number },
): { x: number; y: number } {
  const minX = Math.min(0, rect.width - rect.width * zoom);
  const minY = Math.min(0, rect.height - rect.height * zoom);
  return {
    x: Math.min(0, Math.max(minX, x)),
    y: Math.min(0, Math.max(minY, y)),
  };
}

/** Rechteck aus zwei Punkten, immer positiv und innerhalb des Bildes. */
function boxFromPoints(
  a: { x: number; y: number },
  b: { x: number; y: number },
): Box {
  const x = clamp01(Math.min(a.x, b.x));
  const y = clamp01(Math.min(a.y, b.y));
  return {
    x,
    y,
    width: Math.min(1 - x, Math.abs(b.x - a.x)),
    height: Math.min(1 - y, Math.abs(b.y - a.y)),
  };
}

/**
 * Rahmen um einen angetippten Punkt – der Punkt liegt in der Mitte.
 *
 * Das Seitenverhältnis gilt in *Bildpixeln*, nicht in normalisierten
 * Koordinaten. Ohne diese Umrechnung wäre derselbe Zahlenwert auf einem
 * Hochkantbild ein anderes Rechteck als auf einem querformatigen – der Rahmen
 * käme gestaucht oder gestreckt heraus.
 *
 * Am Bildrand wird der Rahmen nach innen geschoben, nicht verkleinert: ein
 * angeschnittenes Gesicht bekommt so trotzdem einen Rahmen im richtigen
 * Format.
 */
function quickFrame(
  center: { x: number; y: number },
  image: { width: number; height: number },
  zoom: number,
): Box {
  const height = Math.min(1, Math.max(MIN_SIZE, QUICK_HEIGHT / zoom));
  const width = Math.min(1, (height * image.height * QUICK_RATIO) / image.width);
  return {
    x: clamp01(Math.min(center.x - width / 2, 1 - width)),
    y: clamp01(Math.min(center.y - height / 2, 1 - height)),
    width,
    height,
  };
}

function normalizeBox(box: Box): Box {
  const x = clamp01(box.x);
  const y = clamp01(box.y);
  return {
    x,
    y,
    width: Math.min(1 - x, Math.max(MIN_SIZE, box.width)),
    height: Math.min(1 - y, Math.max(MIN_SIZE, box.height)),
  };
}

export interface RegionEditorProps {
  src: string;
  alt: string;
  regions: MediaRegion[];
  editable?: boolean;
  /**
   * Mit eingeschaltetem Bearbeitungsmodus starten – für Ansichten, die es
   * ausschließlich zum Markieren gibt (der Schritt nach dem Hochladen).
   *
   * Auf der Bildseite bleibt er aus: dort wird zehnmal häufiger geschaut als
   * markiert, und ein Rahmen, der sich beim Schauen verschieben lässt, ist
   * eine Falle.
   */
  startEditing?: boolean;
  /**
   * Aufnahmedatum des Bildes. Ist es bekannt, steht am Namensschild das Alter
   * der Person zu diesem Zeitpunkt – „Elli Wagner · 34“.
   */
  takenAt?: FuzzyDate | null;
  /** Diese Person hervorheben (Vollbild-Ansicht aus einer Personenseite). */
  highlightPersonId?: string | null;
  /**
   * Stellen, an denen schon einmal „kein Gesicht" gesagt wurde. Die
   * automatische Erkennung überspringt sie.
   */
  nonFaces?: Array<{ x: number; y: number; width: number; height: number }>;
  /**
   * Diese Markierung beim Öffnen sofort aktivieren und die Zuordnungskarte
   * daneben aufklappen – für Verweise wie „Wer ist das?“ auf der Startseite,
   * die genau zu diesem Gesicht führen.
   */
  focusRegionId?: string | null;
  /**
   * Ein Klick auf eine benannte Markierung *außerhalb* des
   * Bearbeitungsmodus – der Weg zur Person.
   *
   * Beim Ansehen ist die Markierung ein Verweis und kein Werkzeug: sie sagt
   * „das ist Elli", und der nächste Gedanke ist „zeig mir Elli". Zugeordnet,
   * gelöst und verschoben wird eine Ebene tiefer, im Bearbeitungsmodus.
   *
   * Der Aufrufer navigiert selbst, weil nur er weiß, was dabei sonst noch
   * geschlossen werden muss – im Vollbild etwa die Ansicht darüber.
   */
  onOpenPerson?: (person: PersonRef) => void;
  onCreate?: (
    box: Box,
    person: PersonRef | null,
    label: string | null,
  ) => Promise<unknown>;
  onMove?: (regionId: string, box: Box) => Promise<unknown>;
  onAssign?: (
    regionId: string,
    person: PersonRef | null,
    label: string | null,
  ) => Promise<unknown>;
  onDelete?: (regionId: string) => Promise<unknown>;
  /**
   * „Kein Gesicht" – die Markierung löschen *und* die Stelle merken, damit die
   * automatische Erkennung sie nicht wieder setzt.
   *
   * Getrennt von {@link onDelete}, weil beides verschiedene Aussagen sind: ein
   * Rahmen kann auch weg, weil er doppelt ist oder schlecht sitzt. Nur die
   * ausdrückliche Absage taugt als Lehre.
   */
  onNotAFace?: (regionId: string) => Promise<unknown>;
  /**
   * Gefundene Gesichter übernehmen. Ist der Rückruf gesetzt, erscheint der
   * Knopf „Gesichter automatisch erkennen".
   *
   * Gerechnet wird hier drinnen – die Komponente hat das geladene `<img>` schon
   * in der Hand, ein zweites Laden derselben Datei wäre Verschwendung.
   * Übergeben wird nur, was noch nicht markiert ist; die Liste kann leer sein,
   * und was dann auf dem Bildschirm steht, entscheidet der Aufrufer.
   *
   * Das geladene Bild kommt mit: der Aufrufer kann die neuen Rahmen damit
   * gleich vermessen, ohne dasselbe 2048-px-Bild ein zweites Mal zu holen.
   */
  onDetect?: (faces: DetectedFace[], image: HTMLImageElement) => Promise<unknown>;
  /**
   * Die Erkennung ist gescheitert – Modell nicht geladen, kein WebGL, kein
   * Speicher. Die Meldung gehört zum Aufrufer, damit hier keine zweite
   * Meinung darüber entsteht, wie ein Fehler aussieht.
   */
  onDetectError?: (error: unknown) => void;
  /**
   * Meldet, ob im Bild gerade gearbeitet wird – eine Markierung ist gewählt,
   * der Markiermodus läuft oder ein aufgezogener Rahmen wartet auf einen Namen.
   *
   * In diesen Momenten gehören die Pfeiltasten hierher: sie verschieben die
   * Markierung. Der Aufrufer erkennt daran, dass er sie nicht für etwas
   * anderes belegen darf.
   */
  onBusyChange?: (busy: boolean) => void;
  className?: string;
  imageClassName?: string;  /**
   * Das Bild in einen Fotoabzug legen – weißes Papier ringsum, Bildunterschrift
   * darunter.
   *
   * Der Abzug gehört hierher und nicht um das ganze Bauteil: in 2d steht die
   * Werkzeugzeile („Markierungen bearbeiten", „Gesichter automatisch erkennen") *neben*
   * dem Abzug, nicht auf dem Papier. Lag sie mit im Rahmen, sah der Abzug aus
   * wie ein Fenster mit Symbolleiste – und das Papier wuchs auf die ganze
   * Spaltenbreite, während das Bild in der Mitte klein blieb.
   */
  framed?: boolean;
  /** Bildunterschrift im Abzug. Nur mit `framed`. */
  caption?: React.ReactNode;
  /**
   * Ab 1800px das Passepartout aus 2d XL unter den Abzug legen, samt der
   * Zeile „Originalgröße … · größer wird der Scan nicht schärfer". Nur mit
   * `framed`.
   */
  mat?: boolean;

}

/**
 * Bild mit Markierungen. Im Bearbeitungsmodus zieht man ein Rechteck auf,
 * ordnet eine Person zu, verschiebt und skaliert vorhandene Markierungen.
 *
 * Koordinaten sind durchgehend auf 0..1 normalisiert – dieselben Werte gelten
 * für Thumbnail, mittlere Größe und Original.
 *
 * Gezoomt wird per CSS-Transform auf dem Rahmen. Das ist bewusst so: eine
 * Transform ändert das Layout nicht, der Ausschnitt behält also die eingepasste
 * Bildgröße, und `getBoundingClientRect()` des Rahmens liefert bereits die
 * transformierten Maße – `toNormalized` rechnet damit ohne eine einzige
 * Zusatzzeile weiter korrekt, egal wie weit hineingezoomt ist.
 *
 * Skaliert wird dabei ausschließlich das Bild. Rahmen, Namensschilder und
 * Griffe liegen in einer eigenen Ebene *über* der Transform und werden bei
 * jeder Zoomstufe in Bildschirmpixeln neu berechnet. Vorher hingen sie mit im
 * skalierten Teilbaum und wurden gegenskaliert – der Browser hat sie damit
 * verkleinert gezeichnet und wieder aufgeblasen, Linien und Schrift wurden
 * unscharf. Neu gerechnet statt hochskaliert heißt: 2-px-Linie bleibt eine
 * 2-px-Linie, 11-px-Schrift bleibt scharf.
 */
/**
 * Die Fläche, auf der das Bild liegt: nackt (Leuchtkasten, Aufnahme-Schritt)
 * oder als Fotoabzug (Bildseite, 2d).
 *
 * `w-fit` ist der eigentliche Punkt: der Abzug legt sich *ums Bild* und nicht
 * über die ganze Spalte. Vorher war das Papier so breit wie der Bildschirm,
 * das Bild stand mittig darin – und dazwischen lagen zweihundert Pixel weißer
 * Rand, die aussahen wie ein Fehler.
 *
 * Ab sehr breiten Schirmen kommt das Passepartout aus 2d XL dazu: eine
 * getönte, leicht eingesenkte Fläche, auf der der Abzug in Originalgröße
 * steht. Der Grund steht im Entwurf selbst – ein Scan von 1200 × 800 wird auf
 * einem 27-Zoll-Monitor nicht schöner, wenn man ihn auf 2000px zieht. Der
 * gewonnene Platz wird also Rand, nicht Vergrößerung. Dass das Bild bei 100 %
 * aufhört, war hier schon immer so (`w-auto`, keine Streckung); neu ist, dass
 * die Fläche darum das erklärt statt es wie einen Fehler aussehen zu lassen.
 */
function ImageSurface({
  framed,
  mat,
  caption,
  note,
  children,
}: {
  framed: boolean;
  mat: boolean;
  caption?: React.ReactNode;
  note?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (!framed) return <div className="flex justify-center">{children}</div>;

  const frame = (
    <PhotoFrame
      size="lg"
      taped
      caption={caption}
      // 16px ringsum wie in 2d; der Fuß bleibt schmaler, weil die
      // Bildunterschrift ihre eigene Höhe mitbringt – zusammen ergibt das die
      // 44px des Entwurfs.
      className={cn(
        // Die Höchstbreite bremst die Richtung, in die niemand will: ein
        // 4000px-Scan soll auf einem weiten Schirm nicht ins Unendliche laufen.
        // Ab 1800px übernimmt das Passepartout diese Aufgabe – dort begrenzt
        // die Spaltenbreite, und der Abzug steht in Originalgröße darin.
        "mx-auto w-fit max-w-full [--photo-foot:14px] [--photo-pad:16px] lg:max-w-[1100px]",
        mat && "3xl:max-w-full",
        // Auf dem Passepartout ist der Abzug ein Gegenstand mit Abstand zur
        // Wand – deshalb dort der tiefere Schatten und 18px Papier.
        mat && "3xl:[--photo-pad:18px] 3xl:[--photo-foot:16px]",
      )}
    >
      <div className="flex justify-center">{children}</div>
    </PhotoFrame>
  );

  if (!mat) return frame;

  return (
    /* Das Passepartout beginnt schon auf dem kleinen Desktop und wächst mit:
       ab lg 20px Rand, ab 2xl 24px, ab 1800px die 32px des Entwurfs. Es stand
       früher erst ab 1800px, weil 32px ringsum auf einem schmalen Schirm dem
       Bild zu viel wegnehmen – aber das ist ein Argument gegen *diesen Wert*,
       nicht gegen die Fläche. Der getönte Grund ordnet den Abzug ein, sobald
       neben ihm überhaupt Platz ist, und das ist ab lg der Fall. */
    <div
      className={cn(
        'flex flex-col items-center gap-4',
        'lg:rounded-[18px] lg:bg-sunken lg:p-5 lg:shadow-[inset_0_1px_3px_var(--mat-shadow)]',
        '2xl:p-6 3xl:p-8',
      )}
    >
      {frame}
      {note}
    </div>
  );
}

export function RegionEditor({
  src,
  alt,
  regions,
  editable = false,
  startEditing = false,
  takenAt = null,
  highlightPersonId = null,
  focusRegionId = null,
  onOpenPerson,
  onCreate,
  onMove,
  onAssign,
  onDelete,
  onDetect,
  onDetectError,
  onNotAFace,
  onBusyChange,
  nonFaces = [],
  className,
  imageClassName,
  framed = false,
  mat = false,
  caption,
}: RegionEditorProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<DragState | null>(null);

  /**
   * Der Bearbeitungsmodus für Markierungen: nur hier lassen sich Rahmen
   * ziehen, verschieben und in der Größe ändern.
   *
   * Getrennt vom Ansehen, weil beides sonst ineinanderläuft: auf der Bildseite
   * lag ein Rahmen unter jedem Finger, und ein Wischen über das Foto hat
   * unbemerkt ein Gesicht verschoben. Wer bearbeiten will, sagt das einmal –
   * danach ist jede Bewegung im Bild gewollt.
   */
  const [editMode, setEditMode] = useState(startEditing);

  /**
   * Ob die Rahmen über dem Bild liegen. Fensterweit und nicht je Bild – siehe
   * {@link useRegionVisibility}.
   */
  const [showRegions, setShowRegions] = useRegionVisibility();
  const [draft, setDraft] = useState<Box | null>(null);
  const [pendingBox, setPendingBox] = useState<Box | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [liveBox, setLiveBox] = useState<{ id: string; box: Box } | null>(null);
  /**
   * Welche Markierung gerade festgehalten wird. Nur für den Zeiger: die
   * geschlossene Hand muss schon beim Drücken erscheinen, nicht erst bei der
   * ersten Bewegung – `liveBox` entsteht dafür zu spät.
   */
  const [grabbedId, setGrabbedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Läuft die automatische Erkennung? `done` zählt die Durchgänge mit; bei 0
   * wird noch das Modell geladen, und genau das ist der Teil, der wartet.
   */
  const [detecting, setDetecting] = useState<{ done: number; total: number } | null>(null);
  /**
   * Zeigerposition im Fenster, an der die Zuordnungskarte erscheint. Sie wird
   * beim Loslassen bzw. beim Anklicken einer Markierung gemerkt – die Karte
   * soll dort auftauchen, wo die Hand gerade ist.
   */
  const [anchor, setAnchor] = useState<CursorPoint | null>(null);
  /**
   * Ob das Bild schon steht. Vorher hat der Rahmen noch nicht seine endgültige
   * Größe – eine daraus berechnete Position wäre daneben.
   */
  const [imageReady, setImageReady] = useState(false);

  /**
   * Layoutgröße des eingepassten Bildes in CSS-Pixeln, ohne Zoom. Aus ihr
   * entstehen die Pixelmaße der Markierungsebene. Der ResizeObserver liefert
   * Layoutmaße, die Transform auf dem Rahmen verfälscht sie also nicht.
   */
  const [base, setBase] = useState({ width: 0, height: 0 });

  const [view, setView] = useState<View>(FIT);
  const [panning, setPanning] = useState(false);
  const zoomRef = useRef(1);
  zoomRef.current = view.zoom;

  /** Alle gedrückten Zeiger – für die Zwei-Finger-Geste. */
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const panRef = useRef<{
    pointerId: number;
    start: { x: number; y: number };
    startPan: { x: number; y: number };
  } | null>(null);
  const pinchRef = useRef<{
    startDist: number;
    startZoom: number;
    startPan: { x: number; y: number };
    startCenter: { x: number; y: number };
  } | null>(null);

  const selected = regions.find((region) => region.id === selectedId) ?? null;
  const zoomed = view.zoom > 1;

  /**
   * Reagiert diese Markierung überhaupt auf einen Klick?
   *
   * Im Bearbeitungsmodus jede – dort ist sie ein Werkzeug. Beim Ansehen nur
   * die benannten, und auch die nur, wenn der Aufrufer weiß, wohin: eine
   * namenlose Markierung hat keine Personenseite, auf die sie führen könnte.
   */
  const isActionable = (region: MediaRegion) =>
    editable && editMode ? true : Boolean(region.person && onOpenPerson);

  /* Wird hier gerade gearbeitet? Siehe {@link RegionEditorProps.onBusyChange}. */
  const editing = Boolean(selectedId) || editMode || Boolean(pendingBox);
  useEffect(() => {
    onBusyChange?.(editing);
  }, [editing, onBusyChange]);

  /**
   * Rahmen verstecken – und alles beenden, was ohne sie keinen Sinn ergibt.
   *
   * Im Zeichenmodus weiterzumachen, während die Rahmen unsichtbar sind, hieße
   * blind zu markieren: man sähe weder, was schon da ist, noch was man gerade
   * zieht. Eine Handlung, eine Folge – wer ausblendet, will ansehen.
   */
  const hideRegions = useCallback(() => {
    setShowRegions(false);
    setEditMode(false);
    setSelectedId(null);
    setPendingBox(null);
  }, [setShowRegions]);

  const toNormalized = useCallback(
    (event: { clientX: number; clientY: number }) => {
      const rect = frameRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
      return {
        x: clamp01((event.clientX - rect.left) / rect.width),
        y: clamp01((event.clientY - rect.top) / rect.height),
      };
    },
    [],
  );

  useEffect(() => {
    const node = imageRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setBase({ width: rect.width, height: rect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [src]);

  // ── Zoom ─────────────────────────────────────────────────────────────────

  /**
   * Zoomt auf einen Punkt zu: der Bildpunkt unter `focus` (Fensterkoordinaten)
   * bleibt stehen, alles andere wandert darum herum. Ohne `focus` wird die Mitte
   * des Ausschnitts festgehalten.
   */
  const applyZoom = useCallback(
    (next: number | ((current: number) => number), focus?: CursorPoint) => {
      const rect = viewportRef.current?.getBoundingClientRect();
      setView((prev) => {
        const zoom = clampZoom(
          typeof next === "function" ? next(prev.zoom) : next,
        );
        if (!rect || zoom <= 1) return FIT;
        const fx = focus ? focus.x - rect.left : rect.width / 2;
        const fy = focus ? focus.y - rect.top : rect.height / 2;
        const ratio = zoom / prev.zoom;
        return {
          zoom,
          ...clampPan(
            fx - (fx - prev.x) * ratio,
            fy - (fy - prev.y) * ratio,
            zoom,
            rect,
          ),
        };
      });
    },
    [],
  );

  // Anderes Bild, andere Ansicht – sonst hängt man im Vollbild beim Blättern
  // im Ausschnitt des vorigen Fotos fest.
  useEffect(() => {
    setView(FIT);
    pointersRef.current.clear();
    panRef.current = null;
    pinchRef.current = null;
  }, [src]);

  /**
   * Rad: mit Strg/⌘ zoomen (so kennt man es aus jedem Bildbetrachter), ohne
   * Zusatztaste nur dann etwas tun, wenn wirklich hineingezoomt ist – sonst
   * würde das Scrollen der Seite unter der Maus klemmen.
   */
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        applyZoom((current) => current * Math.exp(-event.deltaY / 260), {
          x: event.clientX,
          y: event.clientY,
        });
        return;
      }
      if (zoomRef.current <= 1) return;
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      setView((prev) => ({
        zoom: prev.zoom,
        ...clampPan(
          prev.x - event.deltaX,
          prev.y - event.deltaY,
          prev.zoom,
          rect,
        ),
      }));
    };

    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [applyZoom]);

  /** +, − und 0 wie in jedem Betrachter. */
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target?.isContentEditable) return;

      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        applyZoom((current) => current * ZOOM_STEP);
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        applyZoom((current) => current / ZOOM_STEP);
      } else if (event.key === "0") {
        event.preventDefault();
        setView(FIT);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [applyZoom]);

  // ── Zeiger: Zoomgesten ───────────────────────────────────────────────────
  //
  // Die Buchführung über die gedrückten Zeiger läuft in der *Capture*-Phase.
  // Grund: `beginMove` auf einer Markierung ruft `stopPropagation()`, in der
  // Bubble-Phase käme hier also nichts an – zwei Finger auf einem Gesicht
  // würden dann nicht zoomen.

  const trackDown = (event: React.PointerEvent) => {
    if ((event.target as HTMLElement).closest("[data-zoom-ui]")) return;
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    if (pointersRef.current.size !== 2) return;

    const [a, b] = [...pointersRef.current.values()];
    if (!a || !b) return;
    // Zwei Finger heißen zoomen, nicht zeichnen: ein angefangenes Rechteck
    // wird verworfen, statt beim Spreizen mitzuwachsen.
    dragRef.current = null;
    panRef.current = null;
    setDraft(null);
    setPanning(false);
    pinchRef.current = {
      startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      startZoom: view.zoom,
      startPan: { x: view.x, y: view.y },
      startCenter: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  };

  const trackMove = (event: React.PointerEvent) => {
    if (pointersRef.current.has(event.pointerId)) {
      pointersRef.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
    }
    const pinch = pinchRef.current;
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!pinch || !rect) return;

    const [a, b] = [...pointersRef.current.values()];
    if (!a || !b) return;

    const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
    const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const zoom = clampZoom(pinch.startZoom * (dist / pinch.startDist));
    if (zoom <= 1) {
      setView(FIT);
      return;
    }
    const ratio = zoom / pinch.startZoom;
    const fx = pinch.startCenter.x - rect.left;
    const fy = pinch.startCenter.y - rect.top;
    setView({
      zoom,
      ...clampPan(
        fx - (fx - pinch.startPan.x) * ratio + (center.x - pinch.startCenter.x),
        fy - (fy - pinch.startPan.y) * ratio + (center.y - pinch.startCenter.y),
        zoom,
        rect,
      ),
    });
  };

  const trackUp = (event: React.PointerEvent) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
  };

  // ── Zeiger: Verschieben ──────────────────────────────────────────────────

  const beginPan = (event: React.PointerEvent) => {
    if ((event.target as HTMLElement).closest("[data-zoom-ui]")) return;
    // Zeichnen, Verschieben einer Markierung und Zoomen haben Vorrang.
    if (dragRef.current || pinchRef.current) return;
    if (!zoomed) return;
    // Im Markiermodus zieht man Rechtecke; verschoben wird mit Shift oder der
    // mittleren Maustaste.
    if (editMode && !event.shiftKey && event.button !== 1) return;

    panRef.current = {
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      startPan: { x: view.x, y: view.y },
    };
    setPanning(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const movePan = (event: React.PointerEvent) => {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId || pinchRef.current) return;
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    setView((prev) => ({
      zoom: prev.zoom,
      ...clampPan(
        pan.startPan.x + (event.clientX - pan.start.x),
        pan.startPan.y + (event.clientY - pan.start.y),
        prev.zoom,
        rect,
      ),
    }));
  };

  const endPan = (event: React.PointerEvent) => {
    if (panRef.current?.pointerId !== event.pointerId) return;
    panRef.current = null;
    setPanning(false);
  };

  // ── Zeiger: Markieren ────────────────────────────────────────────────────

  const beginDraw = (event: React.PointerEvent) => {
    if (!editable || !editMode || pendingBox) return;
    if ((event.target as HTMLElement).closest("[data-region]")) return;
    // Shift und mittlere Maustaste sind zum Verschieben reserviert.
    if (event.shiftKey || event.button === 1) return;
    if (pinchRef.current || pointersRef.current.size > 1) return;

    const origin = toNormalized(event);
    dragRef.current = {
      pointerId: event.pointerId,
      kind: "draw",
      origin,
      startBox: { ...origin, width: 0, height: 0 },
    };
    setDraft({ ...origin, width: 0, height: 0 });
    setSelectedId(null);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const beginMove = (event: React.PointerEvent, region: MediaRegion) => {
    if (pendingBox) return;
    if (pinchRef.current || pointersRef.current.size > 1) return;
    // Shift und mittlere Maustaste verschieben den Ausschnitt – auch wenn der
    // Zeiger dabei auf einer Markierung liegt. Ohne diese Ausnahme wäre bei
    // starkem Zoom kaum noch eine freie Bildfläche zum Greifen übrig.
    if (event.shiftKey || event.button === 1) return;
    // Tut die Markierung nichts, lässt sie den Zeiger durch – das Bild
    // darunter bleibt zum Verschieben greifbar.
    if (!isActionable(region)) return;
    event.stopPropagation();
    // Beim Ansehen bleibt es beim Klick, und der führt zur Person. Zuordnen,
    // Verschieben und Skalieren gibt es nur im Bearbeitungsmodus – sonst war
    // beides dasselbe Antippen mit sehr verschiedenen Folgen.
    if (!editable || !editMode) return;
    setSelectedId(region.id);
    setAnchor({ x: event.clientX, y: event.clientY });
    if (!onMove) return;

    setGrabbedId(region.id);
    dragRef.current = {
      pointerId: event.pointerId,
      kind: "move",
      origin: toNormalized(event),
      startBox: region,
      regionId: region.id,
    };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  };

  const beginResize = (
    event: React.PointerEvent,
    region: MediaRegion,
    handle: Handle,
  ) => {
    if (!editable || !editMode || !onMove) return;
    event.stopPropagation();
    setSelectedId(region.id);
    dragRef.current = {
      pointerId: event.pointerId,
      kind: "resize",
      handle,
      origin: toNormalized(event),
      startBox: region,
      regionId: region.id,
    };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const point = toNormalized(event);

    if (drag.kind === "draw") {
      setDraft(boxFromPoints(drag.origin, point));
      return;
    }

    const dx = point.x - drag.origin.x;
    const dy = point.y - drag.origin.y;

    if (drag.kind === "move") {
      const box: Box = {
        x: clamp01(Math.min(drag.startBox.x + dx, 1 - drag.startBox.width)),
        y: clamp01(Math.min(drag.startBox.y + dy, 1 - drag.startBox.height)),
        width: drag.startBox.width,
        height: drag.startBox.height,
      };
      setLiveBox({ id: drag.regionId!, box });
      return;
    }

    // Skalieren: die gegenüberliegende Ecke bleibt stehen.
    const { x, y, width, height } = drag.startBox;
    const corners: Record<
      Handle,
      [{ x: number; y: number }, { x: number; y: number }]
    > = {
      nw: [
        { x: x + width, y: y + height },
        { x: x + dx, y: y + dy },
      ],
      ne: [
        { x, y: y + height },
        { x: x + width + dx, y: y + dy },
      ],
      se: [
        { x, y },
        { x: x + width + dx, y: y + height + dy },
      ],
      sw: [
        { x: x + width, y },
        { x: x + dx, y: y + height + dy },
      ],
    };
    const [fixed, moving] = corners[drag.handle ?? "se"];
    setLiveBox({ id: drag.regionId!, box: boxFromPoints(fixed, moving) });
  };

  const endDrag = async (event: React.PointerEvent) => {
    // Vor der Zeigerprüfung: die Hand darf unter keinen Umständen geschlossen
    // hängen bleiben, auch nicht bei einem abgebrochenen Zeiger.
    setGrabbedId(null);

    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;

    if (drag.kind === "draw") {
      const box = draft;
      setDraft(null);

      // Zu klein zum Ziehen heißt: es war ein Tippen. Statt die Eingabe zu
      // verwerfen – so war es vorher – entsteht daraus ein Rahmen um den
      // getroffenen Punkt. Auf dem Telefon ist das der schnellere Weg:
      // antippen statt ein Rechteck mit dem Finger aufziehen, der dabei genau
      // das Gesicht verdeckt.
      const tapped = !box || box.width < MIN_SIZE || box.height < MIN_SIZE;
      const image = imageRef.current;
      const next = tapped
        ? quickFrame(
            drag.origin,
            {
              // Sind die Bildmaße noch unbekannt, wird das Bild als quadratisch
              // angenommen – dann stimmt wenigstens die Mitte.
              width: image?.naturalWidth || 1,
              height: image?.naturalHeight || 1,
            },
            view.zoom,
          )
        : box;

      if (next && next.width >= MIN_SIZE && next.height >= MIN_SIZE) {
        setPendingBox(normalizeBox(next));
        setAnchor({ x: event.clientX, y: event.clientY });
      }
      return;
    }

    const live = liveBox;
    setLiveBox(null);
    if (!live || !onMove) return;
    const box = normalizeBox(live.box);
    // Nur speichern, wenn sich wirklich etwas verschoben hat.
    const same =
      Math.abs(box.x - drag.startBox.x) < 0.001 &&
      Math.abs(box.y - drag.startBox.y) < 0.001 &&
      Math.abs(box.width - drag.startBox.width) < 0.001 &&
      Math.abs(box.height - drag.startBox.height) < 0.001;
    if (same) return;

    setBusy(true);
    try {
      await onMove(live.id, box);
    } finally {
      setBusy(false);
    }
  };

  /** Doppelklick: heranholen bzw. wieder einpassen. */
  const handleDoubleClick = (event: React.MouseEvent) => {
    if (editMode || pendingBox) return;
    if ((event.target as HTMLElement).closest("[data-zoom-ui]")) return;
    if (zoomed) setView(FIT);
    else applyZoom(2.5, { x: event.clientX, y: event.clientY });
  };

  // ── Tastatur ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!editable || !selected || !onMove) return;

    const handler = async (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      if (event.key === "Escape") {
        setSelectedId(null);
        setAnchor(null);
        return;
      }
      if ((event.key === "Delete" || event.key === "Backspace") && onDelete) {
        event.preventDefault();
        await onDelete(selected.id);
        setSelectedId(null);
        return;
      }

      const arrows: Record<string, [number, number]> = {
        ArrowLeft: [-NUDGE, 0],
        ArrowRight: [NUDGE, 0],
        ArrowUp: [0, -NUDGE],
        ArrowDown: [0, NUDGE],
      };
      const delta = arrows[event.key];
      if (!delta) return;
      // Verschieben ist Bearbeiten – auch mit der Tastatur. Escape und Löschen
      // bleiben unabhängig davon: das eine schließt nur die Auswahl, das
      // andere ist ein ausdrücklicher Griff und kein Verrutschen.
      if (!editMode) return;
      event.preventDefault();

      const [dx, dy] = delta;
      const box: Box = event.shiftKey
        ? // Mit Shift die Größe ändern statt zu verschieben.
          normalizeBox({
            ...selected,
            width: selected.width + dx * 2,
            height: selected.height + dy * 2,
          })
        : normalizeBox({
            ...selected,
            x: Math.min(selected.x + dx, 1 - selected.width),
            y: Math.min(selected.y + dy, 1 - selected.height),
          });
      await onMove(selected.id, box);
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editable, editMode, selected, onMove, onDelete]);

  // ── Verweis auf eine bestimmte Markierung ────────────────────────────────

  useEffect(() => {
    const node = imageRef.current;
    if (!node) return;
    if (node.complete && node.naturalWidth > 0) {
      setImageReady(true);
      return;
    }
    setImageReady(false);
    const onLoad = () => setImageReady(true);
    node.addEventListener("load", onLoad);
    return () => node.removeEventListener("load", onLoad);
  }, [src]);

  /**
   * Pro Verweis nur einmal aufklappen. Sonst käme die Karte nach dem
   * Zuordnen – die Liste der Markierungen ändert sich dabei – gleich wieder.
   */
  const focusedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!focusRegionId) {
      focusedRef.current = null;
      return;
    }
    if (focusedRef.current === focusRegionId || !imageReady) return;
    const region = regions.find((entry) => entry.id === focusRegionId);
    if (!region) return;
    focusedRef.current = focusRegionId;
    // Von außen wird auf genau diese Markierung gezeigt – sie zu verstecken
    // wäre die falsche Antwort auf „zeig mir das".
    setShowRegions(true);
    // „Wer ist das?" führt hierher, um einen Namen einzutragen. Der Verweis
    // bringt den Bearbeitungsmodus deshalb gleich mit – sonst landete man vor
    // einem Rahmen, an dem sich nichts machen lässt.
    if (editable) setEditMode(true);

    // Erst das Bild in den Blick holen, dann messen: sprunghaft gescrollt wird
    // synchron, die Rahmenmaße stimmen also direkt danach.
    viewportRef.current?.scrollIntoView({ block: "center" });
    const rect = frameRef.current?.getBoundingClientRect();
    setSelectedId(region.id);
    // Die Karte hängt an der unteren rechten Ecke der Markierung – so bleibt
    // das Gesicht frei, um das es gerade geht.
    if (rect) {
      setAnchor({
        x: rect.left + (region.x + region.width) * rect.width,
        y: rect.top + (region.y + region.height) * rect.height,
      });
    }
  }, [editable, focusRegionId, imageReady, regions, setShowRegions]);

  // ── Automatische Erkennung ───────────────────────────────────────────────

  /**
   * Gesichter suchen und die Funde nach oben geben.
   *
   * Gerechnet wird auf dem angezeigten `<img>`, nicht auf einem zweiten Ladeweg:
   * das Bild liegt entschlüsselt im Speicher, kommt vom eigenen Server (die
   * Zeichenfläche bleibt also lesbar) und ist mit 2048 px die größte Ableitung –
   * mehr Auflösung, als das Modell braucht.
   */
  const runDetection = useCallback(async () => {
    const node = imageRef.current;
    if (!node || !onDetect || detecting) return;

    // Was die Erkennung setzt, soll man auch entstehen sehen – sonst meldet
    // sie „12 Gesichter gefunden" auf ein Bild, das unverändert aussieht.
    setShowRegions(true);
    setDetecting({ done: 0, total: DETECTION_PASSES });
    try {
      const faces = await detectFaces(node, {
        onProgress: (done, total) => setDetecting({ done, total }),
      });
      // Ein zweiter Durchlauf soll nichts verdoppeln – weder die Rahmen, die
      // jemand von Hand gezogen hat, noch die Stellen, an denen schon einmal
      // „kein Gesicht" gesagt wurde.
      await onDetect(withoutExisting(faces, [...regions, ...nonFaces]), node);
    } catch (error) {
      onDetectError?.(error);
    } finally {
      setDetecting(null);
    }
  }, [detecting, onDetect, onDetectError, regions, setShowRegions]);

  // ── Darstellung ──────────────────────────────────────────────────────────

  /**
   * Maße einer Markierung in Pixeln des Ausschnitts: normalisierte Koordinate
   * mal eingepasste Bildgröße mal Zoom, verschoben um den Bildausschnitt. Damit
   * folgt die Ebene dem Bild, ohne selbst skaliert zu werden.
   */
  const boxStyle = (box: Box) => ({
    left: `${view.x + box.x * base.width * view.zoom}px`,
    top: `${view.y + box.y * base.height * view.zoom}px`,
    width: `${box.width * base.width * view.zoom}px`,
    height: `${box.height * base.height * view.zoom}px`,
  });

  /**
   * Der Hinweis unter dem Abzug – aber nur, wenn er zutrifft.
   *
   * `base` ist die Layoutgröße des eingepassten Bildes, `naturalWidth` die
   * echte Pixelbreite. Sind sie gleich, steht der Scan in Originalgröße, und
   * die Zeile aus 2d XL erklärt, warum er nicht weiterwächst. Wird das Bild
   * dagegen gerade verkleinert angezeigt, wäre derselbe Satz falsch – dann
   * steht dort nichts.
   */
  const natural = imageRef.current;
  const atOriginal =
    imageReady &&
    base.width > 0 &&
    Boolean(natural?.naturalWidth) &&
    base.width >= natural!.naturalWidth - 1;
  const sizeNote = atOriginal ? (
    <p className="flex items-center gap-2 font-mono text-[12px] text-ink-4">
      <Info className="size-3.5 shrink-0" aria-hidden />
      Originalgröße {natural!.naturalWidth} × {natural!.naturalHeight} · größer wird der
      Scan nicht schärfer
    </p>
  ) : null;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {editable ? (
        /* Genau die Zeile aus 2d: die Hauptaktion tintenfarben, die Erkennung
           in Gold (sie nimmt Arbeit ab, deshalb der Akzent), der Zähler leise
           am rechten Rand. */
        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="primary"
            size="chrome"
            className={cn(editMode && "ring-2 ring-accent ring-offset-2 ring-offset-canvas")}
            onClick={() => {
              // Wer markieren will, braucht die vorhandenen Rahmen – sonst
              // zieht er ein zweites um dasselbe Gesicht.
              setShowRegions(true);
              setEditMode((value) => !value);
              setSelectedId(null);
              setPendingBox(null);
            }}
          >
            <ScanFace aria-hidden />
            {editMode ? "Speichern" : "Markierungen bearbeiten"}
          </Button>

          {/* Die Erkennung setzt nur Rahmen. Der Knopf steht deshalb neben
              „Markierungen bearbeiten" und nicht an dessen Stelle: er nimmt die
              Fleißarbeit ab, die Namen kommen danach auf demselben Weg wie
              vorher. Ohne geladenes Bild gäbe es nichts zu durchsuchen. */}
          {onDetect ? (
            <Button
              variant="ghost"
              size="chrome"
              className="bg-accent-soft font-semibold text-accent-strong hover:bg-accent-soft hover:brightness-[0.98]"
              loading={Boolean(detecting)}
              disabled={!imageReady}
              onClick={() => void runDetection()}
              title={
                detectorReady()
                  ? "Setzt Rahmen um die gefundenen Gesichter. Die Namen kommen danach von dir."
                  : "Setzt Rahmen um die gefundenen Gesichter. Beim ersten Mal wird ein 12-MB-Erkenner geladen – von diesem Server, nicht von außen."
              }
            >
              {detecting ? null : <Sparkles aria-hidden />}
              {detecting
                ? detecting.done === 0
                  ? "Erkenner wird geladen …"
                  : // Ausdrücklich „Ausschnitt", nicht bloß „37/55": die nackte
                    // Zahl liest sich wie „37 Gesichter gefunden" und lässt
                    // ratlos zurück, warum sie so hoch zählt. Gezählt werden
                    // die Bildausschnitte, die das Modell durchsucht.
                    `sucht … Ausschnitt ${detecting.done} von ${detecting.total}`
                : "Gesichter automatisch erkennen"}
            </Button>
          ) : null}

          <p className="ml-auto text-[13px] text-ink-3">
            {editMode
              ? "Auf das Gesicht tippen – oder ein Rechteck ziehen. Vorhandene Rahmen lassen sich fassen und verschieben." +
                (zoomed ? " Zum Verschieben des Ausschnitts Shift halten." : "")
              : `${regions.length} ${regions.length === 1 ? "Markierung" : "Markierungen"}` +
                (regions.some((region) => !region.person)
                  ? ` · ${regions.filter((region) => !region.person).length} ohne Namen`
                  : "")}
          </p>
        </div>
      ) : null}

      <ImageSurface framed={framed} mat={mat} caption={caption} note={sizeNote}>
        {/* Ausschnitt: behält die eingepasste Bildgröße und beschneidet das
            vergrößerte Bild. Erst ab Zoom > 1 wird beschnitten, damit der
            Schlagschatten im Vollbild bei normaler Ansicht sichtbar bleibt. */}
        <div
          ref={viewportRef}
          // `inline-flex`, nicht `inline-block`: bei zwei geschachtelten
          // Inline-Blöcken hängt unter dem Bild die Zeilenunterlänge als
          // 7-px-Streifen im Ausschnitt – und der Ausschnitt ist genau das
          // Maß, mit dem die Verschiebung begrenzt wird.
          className={cn(
            "relative inline-flex max-w-full touch-none select-none",
            // Beschnitten wird eckig, weil das Bild eckig ist.
            zoomed && "overflow-hidden",
            editMode
              ? "cursor-crosshair"
              : zoomed
                ? panning
                  ? "cursor-grabbing"
                  : "cursor-grab"
                : null,
          )}
          onPointerDownCapture={trackDown}
          onPointerMoveCapture={trackMove}
          onPointerUpCapture={trackUp}
          onPointerCancelCapture={trackUp}
          // Zeichnen, Verschieben und Skalieren hängen am Ausschnitt, nicht am
          // skalierten Rahmen: die Markierungen liegen in einer eigenen Ebene,
          // ihre Zeigerereignisse laufen also nicht mehr durch den Rahmen.
          // Reihenfolge wie zuvor – `beginDraw` zuerst, `beginPan` sieht daran,
          // dass gerade gezeichnet wird, und hält sich heraus.
          onPointerDown={(event) => {
            beginDraw(event);
            beginPan(event);
          }}
          onPointerMove={(event) => {
            handlePointerMove(event);
            movePan(event);
          }}
          onPointerUp={(event) => {
            void endDrag(event);
            endPan(event);
          }}
          onPointerCancel={(event) => {
            void endDrag(event);
            endPan(event);
          }}
          onDoubleClick={handleDoubleClick}
        >
          {/* Der skalierte Teil: nur das Bild. Bewusst ohne `will-change` – das
              hätte den Browser angewiesen, die einmal gezeichnete Fläche bloß
              zu strecken, statt beim Zoomen neu zu zeichnen. Genau daran lag
              die Unschärfe. */}
          <div
            ref={frameRef}
            style={{
              translate: `${view.x}px ${view.y}px`,
              scale: String(view.zoom),
              transformOrigin: "top left",
            }}
            className="relative inline-block max-w-full"
          >
            {/* Ohne eigene Rundung: im Abzug hat das Bild eckige Kanten wie
                jedes andere in diesem Archiv – gerundet ist das Papier
                drumherum (`--photo-radius`), nicht die Aufnahme. Wo dieses
                Bauteil *ohne* Rahmen steht (Vollbild), bringt der Aufrufer
                seine Rundung über `imageClassName` mit. */}
            <img
              ref={imageRef}
              src={src}
              alt={alt}
              draggable={false}
              className={cn("block max-h-[70dvh] w-auto max-w-full", imageClassName)}
            />
          </div>

          {/* Markierungsebene: unskaliert, in Pixeln gerechnet, deshalb bei
              jeder Zoomstufe scharf. `pointer-events-none` gibt die freie
              Bildfläche zum Zeichnen und Verschieben frei; die Rechtecke selbst
              nehmen ihre Ereignisse wieder an. `isolate` hält ihr z-index
              unter der Zoomleiste. */}
          <div className="pointer-events-none absolute inset-0 isolate">
            {/* Vorhandene Markierungen */}
            {showRegions && regions.map((region) => {
              const box = liveBox?.id === region.id ? liveBox.box : region;
              const isSelected = selectedId === region.id;
              const isHighlight =
                Boolean(highlightPersonId) &&
                region.person?.id === highlightPersonId;
              const dimmed = Boolean(highlightPersonId) && !isHighlight;
              // Alter nur anzeigen, wenn es plausibel ist – eine widersprüchliche
              // Zahl am Gesicht wäre schlimmer als keine. An ihre Stelle tritt
              // ein Warnzeichen: dass hier etwas nicht zusammenpasst, gehört
              // ans Gesicht, der ganze Satz dazu in den Tooltip.
              const conflict = region.person
                ? photoDateConflict(region.person, takenAt)
                : null;
              const age = region.person ? photoAge(region.person, takenAt) : null;
              const shownAge = age && !conflict ? age.short : null;

              return (
                <div
                  key={region.id}
                  data-region={region.id}
                  style={boxStyle(box)}
                  // Beim Ansehen sagt der Rahmen, wohin er führt – sonst ist
                  // der Zeigefinger die einzige Auskunft darüber.
                  title={
                    !(editable && editMode) && region.person && onOpenPerson
                      ? `Zur Seite von ${region.person.displayName}`
                      : undefined
                  }
                  onPointerDown={(event) => beginMove(event, region)}
                  onClick={(event) => {
                    if (!isActionable(region)) return;
                    event.stopPropagation();
                    if (editable && editMode) {
                      setSelectedId(region.id);
                      return;
                    }
                    onOpenPerson?.(region.person!);
                  }}
                  className={cn(
                    "pointer-events-auto absolute rounded-md border-2",
                    "transition-[opacity,border-color]",
                    // Aktives bzw. hervorgehobenes Rechteck liegt oben, damit sich
                    // die Namensschilder bei engen Gruppen nicht verdecken.
                    isSelected || isHighlight ? "z-10" : "z-0",
                    // Offene Hand heißt „das kannst du greifen", geschlossene
                    // „du hältst es". Ein Verschiebe-Kreuz sagt zwar dasselbe,
                    // aber die Hand zeigt zusätzlich, ob der Griff sitzt. Nur
                    // im Bearbeitungsmodus – sonst verspricht der Zeiger etwas,
                    // das die Markierung gar nicht tut.
                    editable &&
                      editMode &&
                      onMove &&
                      (grabbedId === region.id ? "cursor-grabbing" : "cursor-grab"),
                    // Beim Ansehen ist die benannte Markierung ein Verweis:
                    // anklicken führt zur Person, nicht ans Werkzeug.
                    !(editable && editMode) &&
                      region.person &&
                      onOpenPerson &&
                      "cursor-pointer",
                    // Gold heißt „hat einen Namen", dunkel gestrichelt heißt
                    // „offene Frage". Zwei Zustände, zwei Formen – nicht zwei
                    // Farben, die man auf einem alten Foto kaum unterscheidet.
                    // Weiß trug das früher, und weiße Rahmen verschwanden auf
                    // hellen Bildern.
                    isHighlight
                      ? "border-[var(--accent)] ring-2 ring-black/45"
                      : conflict
                        ? "border-[var(--warn)] ring-1 ring-black/50"
                        : region.person
                          ? "border-[var(--accent)] ring-1 ring-black/35"
                          : "border-dashed border-[var(--panel)] bg-[var(--panel)]/10 ring-1 ring-white/30",
                    isSelected &&
                      "border-[var(--accent-strong)] ring-2 ring-[var(--accent)]/50",
                    dimmed && "opacity-55",
                  )}
                >
                  {/* Name unter dem Rechteck, auf eigener Fläche statt direkt aufs Bild */}
                  <span
                    title={conflict?.detail ?? age?.hint}
                    className={cn(
                      "absolute top-full left-0 mt-1 max-w-[11rem] truncate rounded px-1.5 py-0.5",
                      "text-[11px] leading-4 font-medium shadow-panel",
                      conflict
                        ? "bg-[var(--warn-soft)] text-[var(--warn)]"
                        : region.person
                          ? "bg-[var(--accent)] text-[var(--accent-ink)]"
                          : // Die offene Frage trägt das dunkle Schild: sie ist
                            // keine Warnung, sondern die Arbeit, die ansteht.
                            "bg-[var(--panel)] text-[var(--panel-ink)]",
                    )}
                  >
                    {conflict ? (
                      <AlertTriangle
                        className="mr-0.5 inline size-3 align-[-2px]"
                        aria-label={conflict.label}
                      />
                    ) : null}
                    {region.person?.displayName ?? region.label ?? "Wer ist das?"}
                    {shownAge ? (
                      <span
                        className={cn(
                          "ml-1 tabular-nums",
                          isHighlight ? "opacity-80" : "text-ink-3",
                        )}
                      >
                        · {shownAge}
                      </span>
                    ) : null}
                  </span>

                  {/* Griffe zum Skalieren */}
                  {editable && editMode && isSelected && onMove
                    ? (["nw", "ne", "se", "sw"] as Handle[]).map((handle) => (
                        <span
                          key={handle}
                          onPointerDown={(event) =>
                            beginResize(event, region, handle)
                          }
                          className={cn(
                            "absolute size-3 rounded-[3px] border border-black/40 bg-white",
                            handle === "nw" &&
                              "-top-1.5 -left-1.5 cursor-nwse-resize",
                            handle === "ne" &&
                              "-top-1.5 -right-1.5 cursor-nesw-resize",
                            handle === "se" &&
                              "-right-1.5 -bottom-1.5 cursor-nwse-resize",
                            handle === "sw" &&
                              "-bottom-1.5 -left-1.5 cursor-nesw-resize",
                          )}
                        />
                      ))
                    : null}
                </div>
              );
            })}

            {/* Rechteck, das gerade gezogen wird */}
            {draft ? (
              <div
                style={boxStyle(draft)}
                className="absolute rounded-md border-2 border-dashed border-[var(--accent)] bg-[var(--accent)]/15"
              />
            ) : null}

            {/* Neu gezogene Markierung wartet auf eine Zuordnung */}
            {pendingBox ? (
              <div
                style={boxStyle(pendingBox)}
                className="absolute rounded-md border-2 border-[var(--accent)] ring-2 ring-[var(--accent)]/40"
              />
            ) : null}
          </div>

          {/* Zoomleiste. Sie liegt außerhalb des skalierten Rahmens, damit sie
              nicht mitwächst – und trägt `data-zoom-ui`, damit ein Klick darauf
              kein Rechteck aufzieht und nicht verschiebt. */}
          <div
            data-zoom-ui
            className={cn(
              "absolute right-2 bottom-2 z-20 flex items-center gap-0.5 rounded-full",
              "border border-white/15 bg-black/55 p-0.5 text-white backdrop-blur-sm",
              "opacity-70 transition-opacity hover:opacity-100",
              zoomed && "opacity-100",
            )}
          >
            {/* Rahmen zeigen oder verstecken.

                In der Ansichtsleiste und nicht in der Werkzeugzeile darüber:
                die gibt es nur beim Bearbeiten, gerade im Vollbild fehlt sie –
                und dort will man ein Foto am ehesten ohne Rahmen sehen. Sie ist
                auch der richtige Nachbar: Zoom ist genauso eine Frage der
                Ansicht und ändert am Archiv nichts.

                Ein durchgestrichenes Auge statt gar nichts, sobald versteckt
                wird: sonst sieht ein leeres Bild aus wie eines ohne
                Markierungen, und der Weg zurück ist nicht zu erraten. */}
            {regions.length > 0 ? (
              <>
                <button
                  type="button"
                  aria-label={showRegions ? 'Markierungen ausblenden' : 'Markierungen einblenden'}
                  aria-pressed={!showRegions}
                  title={
                    showRegions
                      ? `${regions.length} ${regions.length === 1 ? 'Markierung' : 'Markierungen'} ausblenden`
                      : `${regions.length} ${regions.length === 1 ? 'Markierung' : 'Markierungen'} wieder einblenden`
                  }
                  onClick={() => (showRegions ? hideRegions() : setShowRegions(true))}
                  className={cn(
                    'rounded-full p-1.5 transition-colors hover:bg-white/20',
                    !showRegions && 'bg-white/20',
                  )}
                >
                  {showRegions ? (
                    <Eye className="size-4" aria-hidden />
                  ) : (
                    <EyeOff className="size-4" aria-hidden />
                  )}
                </button>
                <span className="mx-0.5 h-4 w-px bg-white/20" aria-hidden />
              </>
            ) : null}

            <button
              type="button"
              aria-label="Verkleinern"
              disabled={!zoomed}
              onClick={() => applyZoom((current) => current / ZOOM_STEP)}
              className="rounded-full p-1.5 transition-colors hover:bg-white/20 disabled:opacity-40"
            >
              <ZoomOut className="size-4" aria-hidden />
            </button>
            <span className="min-w-[2.75rem] text-center text-[11.5px] tabular-nums">
              {Math.round(view.zoom * 100)} %
            </span>
            <button
              type="button"
              aria-label="Vergrößern"
              disabled={view.zoom >= MAX_ZOOM}
              onClick={() => applyZoom((current) => current * ZOOM_STEP)}
              className="rounded-full p-1.5 transition-colors hover:bg-white/20 disabled:opacity-40"
            >
              <ZoomIn className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Ansicht einpassen"
              title="Einpassen (0)"
              disabled={!zoomed}
              onClick={() => setView(FIT)}
              className="rounded-full p-1.5 transition-colors hover:bg-white/20 disabled:opacity-40"
            >
              <Minimize2 className="size-4" aria-hidden />
            </button>
          </div>
        </div>
      </ImageSurface>

      {/* Zuordnungs-Karte – sie erscheint am Zeiger, nicht am Rechteck, und
          hängt per Portal am Body: vom beschnittenen Ausschnitt und vom Zoom
          bleibt sie damit unberührt. */}
      {pendingBox && onCreate && anchor ? (
        <AssignCard
          at={anchor}
          busy={busy}
          title="Wer ist das?"
          quickPick
          onCancel={() => {
            setPendingBox(null);
            setAnchor(null);
          }}
          onSubmit={async (person, label) => {
            setBusy(true);
            try {
              await onCreate(pendingBox, person, label);
              setPendingBox(null);
              setAnchor(null);
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}

      {selected && editable && editMode && !pendingBox && onAssign && anchor ? (
        <AssignCard
          at={anchor}
          busy={busy}
          title="Markierung"
          initialPerson={selected.person}
          initialLabel={selected.label}
          onCancel={() => {
            setSelectedId(null);
            setAnchor(null);
          }}
          onNotAFace={
            onNotAFace && !selected.person
              ? async () => {
                  setBusy(true);
                  try {
                    await onNotAFace(selected.id);
                    setSelectedId(null);
                    setAnchor(null);
                  } finally {
                    setBusy(false);
                  }
                }
              : undefined
          }
          onDelete={
            onDelete
              ? async () => {
                  setBusy(true);
                  try {
                    await onDelete(selected.id);
                    setSelectedId(null);
                    setAnchor(null);
                  } finally {
                    setBusy(false);
                  }
                }
              : undefined
          }
          onSubmit={async (person, label) => {
            setBusy(true);
            try {
              await onAssign(selected.id, person, label);
              setSelectedId(null);
              setAnchor(null);
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** Kleine Karte am Zeiger: Person zuordnen, benennen, löschen. */
function AssignCard({
  at,
  title,
  busy,
  initialPerson = null,
  initialLabel = null,
  quickPick = false,
  onSubmit,
  onCancel,
  onDelete,
  onNotAFace,
}: {
  at: CursorPoint;
  title: string;
  busy: boolean;
  initialPerson?: PersonRef | null;
  initialLabel?: string | null;
  /**
   * Für neue Markierungen: Die Personensuche steht offen und fokussiert da,
   * man tippt sofort los, und die getroffene Person wird direkt übernommen –
   * ohne den Umweg über „Übernehmen“.
   */
  quickPick?: boolean;
  onSubmit: (person: PersonRef | null, label: string | null) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => Promise<void>;
  /** Nur bei namenlosen Markierungen: „hier ist kein Gesicht". */
  onNotAFace?: () => Promise<void>;
}) {
  const [person, setPerson] = useState<PersonRef | null>(initialPerson);
  const [label, setLabel] = useState(initialLabel ?? "");

  useEffect(() => {
    setPerson(initialPerson);
    setLabel(initialLabel ?? "");
  }, [initialPerson, initialLabel]);

  return (
    <CursorCard at={at}>
      <div
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        // Auf schmalen Fenstern nie breiter als der Bildschirm.
        className="w-[min(19rem,calc(100vw-1.5rem))] rounded-xl border border-line bg-surface p-2.5 shadow-pop"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-[12.5px] font-semibold text-ink">{title}</p>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Abbrechen"
            className="rounded p-0.5 text-ink-3 transition-colors hover:bg-sunken hover:text-ink"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </div>

        <PersonPicker
          value={person}
          defaultOpen={quickPick}
          onChange={(picked) => {
            setPerson(picked);
            if (quickPick && picked && !busy) void onSubmit(picked, null);
          }}
          placeholder="Person suchen…"
        />

        {!person ? (
          <div className="mt-2">
            <Input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="oder Notiz: „Frau links“"
              className="h-11 text-base sm:h-8 sm:text-[13px]"
            />
            <p className="mt-1 flex items-start gap-1 text-[11.5px] text-ink-3">
              <HelpCircle className="mt-px size-3.5 shrink-0" aria-hidden />
              Ohne Person bleibt die Markierung als offene Frage stehen.
            </p>
          </div>
        ) : null}

        {/* Umbrechend statt nebeneinander: „Löschen“, „Später klären“ und
            „Übernehmen“ passen zusammen nicht in eine Zeile und liefen sonst
            über den Kartenrand hinaus. */}
        <div className="mt-2.5 flex flex-wrap items-center justify-end gap-1.5">
          {onDelete ? (
            <Button
              variant="dangerGhost"
              size="sm"
              className="mr-auto"
              onClick={onDelete}
              disabled={busy}
            >
              <Trash2 aria-hidden />
              Löschen
            </Button>
          ) : null}
          {/* Die Absage an die Erkennung, nicht bloß das Wegräumen: die Stelle
              wird gemerkt und beim nächsten Durchlauf übersprungen. */}
          {onNotAFace ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onNotAFace}
              disabled={busy}
              title="Löschen und merken: hier ist kein Gesicht. Die automatische Erkennung setzt an dieser Stelle keinen Rahmen mehr."
            >
              <Ban aria-hidden />
              Kein Gesicht
            </Button>
          ) : null}
          {!person && !label.trim() ? (
            <Button
              variant="secondary"
              size="sm"
              loading={busy}
              onClick={() => onSubmit(null, null)}
            >
              Später klären
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            onClick={() => onSubmit(person, label.trim() || null)}
          >
            <Check aria-hidden />
            Übernehmen
          </Button>
        </div>
      </div>
    </CursorCard>
  );
}
